import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLetterToHeavenList, getFlowerImages } from '../api';
import FamilyPlazaTopSection from './family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from './family-plaza/FamilyPlazaSubHeading';
import '../styles/LetterToHeavenBoardListMobile.css';
import { FaSearch } from 'react-icons/fa';

const LETTERS_PER_PAGE = 9;

// Normalize flower API response
const normalizeFlowers = (raw) => {
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.content) ? raw.content : Array.isArray(raw?.data) ? raw.data : [];
  return arr
    .map((f, index) => ({
      flowerId: Number(
        f.flowerId ?? f.boardStampId ?? f.board_stamp_id ?? f.boardStamp ?? f.id ?? f.flower_id ?? 0
      ),
      imageUrl: f.imageUrl ?? f.flowerImageUrl ?? f.url ?? f.path ?? f.src ?? '',
      name: f.name ?? f.flowerName ?? `Stamp ${index + 1}`,
    }))
    .filter((x) => Boolean(x.flowerId) && Boolean(x.imageUrl));
};

// Normalize letters
const normalizeLetter = (item, flowerMap) => ({
  id: item.id ?? item.letterId ?? item.boardId ?? item.board_id,
  to: item.toName ?? item.recipient ?? item.to ?? '',
  title: item.title ?? '',
  sender: item.userName ?? item.sender ?? item.user ?? '',
  content: item.content ?? '',
  date: item.registDate ?? item.date ?? item.createdAt ?? '',
  boardStamp: Number(item.boardStamp ?? item.board_stamp ?? item.boardStampId ?? item.board_stamp_id ?? 0),
  selectedFlowerUrl: item.flowerImageUrl ?? item.imageUrl ?? flowerMap?.[Number(item.boardStamp ?? 0)] ?? '',
});

const LetterToHeavenBoardListMobile = () => {
  const navigate = useNavigate();

  const [letters, setLetters] = useState([]);
  const [filteredLetters, setFilteredLetters] = useState([]);
  const [allLetters, setAllLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreLetters, setHasMoreLetters] = useState(true);
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [selectedFilter, setSelectedFilter] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');

  const [flowerMap, setFlowerMap] = useState({});
  const [isAllLettersLoaded, setIsAllLettersLoaded] = useState(false);
  const [isPrefetchingAll, setIsPrefetchingAll] = useState(false);

  const latestRequestRef = useRef(0);
  const debounceTimerRef = useRef(null);

  const [loadingMore, setLoadingMore] = useState(false);

  // -------------------- Flower images --------------------
  useEffect(() => {
    let mounted = true;
    const fallbackFlowers = [
      { flowerId: 1, imageUrl: '/heaven/getty-images-145244798.png' },
      { flowerId: 2, imageUrl: '/heaven/getty-images-465710299.png' },
      { flowerId: 3, imageUrl: '/heaven/getty-images-955651052.png' },
      { flowerId: 4, imageUrl: '/heaven/getty-images-907936742.png' },
      { flowerId: 5, imageUrl: '/heaven/getty-images-494124656.png' },
      { flowerId: 6, imageUrl: '/heaven/getty-images-926601494.png' },
      { flowerId: 7, imageUrl: '/heaven/getty-images-537807854.png' },
    ];

    const loadFlowers = async () => {
      try {
        const res = await getFlowerImages();
        const list = normalizeFlowers(res?.data ?? res);
        const flowersToUse = list.length ? list : fallbackFlowers;
        const map = {};
        flowersToUse.forEach((f) => { map[f.flowerId] = f.imageUrl; });
        if (mounted) setFlowerMap(map);
      } catch (err) {
        const map = {};
        fallbackFlowers.forEach((f) => { map[f.flowerId] = f.imageUrl; });
        if (mounted) setFlowerMap(map);
      }
    };

    loadFlowers();
    return () => { mounted = false; };
  }, []);

  // -------------------- Filter mapping --------------------
  const mapFilterToSearchType = (filter) => {
    if (!filter) return 'all';
    const f = filter.toLowerCase().trim();
    switch (f) {
      case '제목':
      case 'title':
        return 'title';
      case '내용':
      case 'content':
        return 'content';
      case '받으시는 분':
      case '받으시는분':
      case 'to':
      case 'toname':
        return 'toName';
      case '보내시는 분':
      case '보내시는분':
      case 'sender':
      case 'user':
        return 'userName';
      case '전체':
      case 'all':
      default:
        return 'all';
    }
  };

  // -------------------- Normalize & Fetch --------------------
  const fetchPage = useCallback(async ({ page = 1, append = false }) => {
    const requestId = ++latestRequestRef.current;
    try {
      setLoading(true);

      const params = {
        page,
        size: LETTERS_PER_PAGE,
        searchType: isSearchActive ? mapFilterToSearchType(selectedFilter) : undefined,
        keyword: isSearchActive ? searchQuery : undefined,
      };

      const res = await getLetterToHeavenList(params);
      const list = Array.isArray(res?.data?.content) ? res.data.content : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      const normalized = list.map((item) => normalizeLetter(item, flowerMap));

      if (requestId !== latestRequestRef.current) return;

      if (append) {
        setLetters((prev) => [...prev, ...normalized]);
        setFilteredLetters((prev) => [...prev, ...normalized]);
      } else {
        setLetters(normalized);
        setFilteredLetters(normalized);
      }

      setHasMoreLetters(list.length === LETTERS_PER_PAGE);
    } catch (err) {
      console.error('Failed to fetch letters', err);
      setLetters([]);
      setFilteredLetters([]);
      setHasMoreLetters(false);
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [flowerMap, isSearchActive, selectedFilter, searchQuery]);

  // -------------------- Fetch ALL letters for client-side search --------------------
  const fetchAllLettersOnce = useCallback(async () => {
    if (isAllLettersLoaded || isPrefetchingAll) return;
    setIsPrefetchingAll(true);
    try {
      const first = await getLetterToHeavenList({ page: 1, size: LETTERS_PER_PAGE });
      const pageData = first.data?.content ?? [];
      const totalPages = first.data?.totalPages ?? 1;
      const collected = [...pageData];

      for (let p = 2; p <= totalPages; p++) {
        try {
          const res = await getLetterToHeavenList({ page: p, size: LETTERS_PER_PAGE });
          collected.push(...(res.data?.content ?? []));
        } catch { }
      }

      const normalizedAll = collected.map((item) => normalizeLetter(item, flowerMap));
      setAllLetters(normalizedAll);
      setIsAllLettersLoaded(true);
    } catch (err) {
      console.error('Failed prefetch all letters', err);
    } finally {
      setIsPrefetchingAll(false);
    }
  }, [flowerMap, isAllLettersLoaded, isPrefetchingAll]);

  // -------------------- Client-side filtering --------------------
  const filterLettersClientSide = (lettersArr, query, filterKey) => {
    if (!query?.trim()) return lettersArr;
    const term = query.toLowerCase().trim();
    switch (filterKey) {
      case 'title':
        return lettersArr.filter(l => l.title?.toLowerCase().includes(term));
      case 'content':
        return lettersArr.filter(l => l.content?.toLowerCase().includes(term));
      case 'toName':
        return lettersArr.filter(l => l.to?.toLowerCase().includes(term));
      case 'userName':
        return lettersArr.filter(l => l.sender?.toLowerCase().includes(term));
      case 'all':
      default:
        return lettersArr.filter(l =>
          l.title?.toLowerCase().includes(term) ||
          l.content?.toLowerCase().includes(term) ||
          l.sender?.toLowerCase().includes(term) ||
          l.to?.toLowerCase().includes(term)
        );
    }
  };

  // -------------------- Search --------------------
  const handleSearch = useCallback(async () => {
    if (!searchQuery?.trim()) {
      setIsSearchActive(false);
      setFilteredLetters(letters);
      return;
    }

    setIsSearchActive(true);

    if (!isAllLettersLoaded) await fetchAllLettersOnce();
    const filterKey = mapFilterToSearchType(selectedFilter);
    const results = filterLettersClientSide(allLetters.length ? allLetters : letters, searchQuery, filterKey);
    setFilteredLetters(results);
    setHasMoreLetters(false);
  }, [searchQuery, selectedFilter, allLetters, letters, isAllLettersLoaded, fetchAllLettersOnce]);


  // -------------------- Manual Search Only (button/enter) --------------------
  useEffect(() => {
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
      setFilteredLetters(letters);
    }
  }, [searchQuery, letters]);


  // -------------------- Input handlers --------------------
  const handleSearchInputChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (!v.trim()) {
      setIsSearchActive(false);
      setFilteredLetters(letters);
    }
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    handleSearch();
  };

  // -------------------- Navigation --------------------
  const handleLetterClick = (letterId) => {
    const current = isSearchActive ? filteredLetters : letters;
    const l = current.find((x) => x.id === letterId);
    if (l) localStorage.setItem('selectedLetterRecipient', l.to || '');
    navigate(`/letter-to-heaven-post-details/${letterId}`);
  };

  const handleWritePost = () => {
    localStorage.removeItem('editingLetterToHeavenData');
    navigate('/letter-to-heaven-writing');
  };

  // const handleLoadMore = () => {
  //   if (isSearchActive) return;
  //   const next = currentPage + 1;
  //   setCurrentPage(next);
  //   fetchPage({ page: next, append: true });
  // };

  const handleLoadMore = async () => {
    if (loadingMore) return; // prevent multiple clicks
    setLoadingMore(true);

    const next = currentPage + 1;
    setCurrentPage(next);

    try {
      await fetchPage({ page: next, append: true });
    } finally {
      setLoadingMore(false);
    }
  };

  // -------------------- Helpers --------------------
  const getStampImage = (letter) => {
    const url = letter.selectedFlowerUrl || '';
    if (!url) return '/heaven/getty-images-145244798.png';
    if (/^(https?:)?\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_BASE_URL ?? ''}${url}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      if (/^\d{4}\.\d{2}\.\d{2}$/.test(dateString)) return dateString;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString.replace(/-/g, '.');
      const d = new Date(dateString);
      return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    } catch { return dateString; }
  };

  const lettersToDisplay = isSearchActive ? filteredLetters : letters;

  // -------------------- Initial fetch --------------------
  useEffect(() => { fetchPage({ page: 1, append: false }); fetchAllLettersOnce(); }, []);

  // -------------------- Render --------------------
  return (
    <div className="letter-to-heaven-board-list-mobile-unique announcement-list-page donate-info-page-pc">
      <FamilyPlazaTopSection />
      <main className="main-content">
        <FamilyPlazaSubHeading heading="하늘로 보내는 편지" />

        {/* Description */}
        <div className="letter-to-heaven-board-list-mobile-description-unique">
          <p>소중한 사람에게 마음으로 보내는 편지</p>
          <p>나의 그립고 소중한 사람에게 마음을 담은 편지를 적어</p>
          <p>하늘로 보내세요</p>
        </div>

        {/* Search & Filter */}
        <div className="letter-to-heaven-board-list-mobile-search-section-unique">
          <div className="letter-to-heaven-board-list-mobile-search-container-unique">
            <input
              type="text"
              placeholder="Search"
              className="letter-to-heaven-board-list-mobile-search-input-unique"
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="letter-to-heaven-board-list-mobile-search-button-unique" onClick={handleSearch}>
            {/* 검색  */}
             <FaSearch />
            </button>
          </div>
          {/* Filter Dropdown (mobile friendly) */}
          <select className="letter-to-heaven-board-list-mobile-search-input-unique" value={selectedFilter} onChange={(e) => handleFilterChange(e.target.value)}>
            {['전체', '제목', '내용', '받으시는 분', '보내시는 분'].map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <button className="letter-to-heaven-board-list-mobile-write-button-unique" onClick={handleWritePost}>
            글쓰기
          </button>
        </div>

        {/* Letters */}
        <div className="letter-to-heaven-board-list-mobile-content-unique">
          {loading ? <p>Loading letters...</p> :
            lettersToDisplay.length ? lettersToDisplay.map(letter => (
              <div key={letter.id} className="letter-to-heaven-board-list-mobile-card-unique" onClick={() => handleLetterClick(letter.id)}>
                <div className="letter-to-heaven-board-list-mobile-card-image-unique">
                  <img src={getStampImage(letter)} alt="Letter to heaven" onError={(e) => { e.currentTarget.src = '/heaven/getty-images-145244798.png'; }} />
                </div>
                <div className="letter-to-heaven-board-list-mobile-card-content-unique">
                  <p className="letter-to-heaven-board-list-mobile-card-to-unique">To. {letter.to}</p>
                  <p className="letter-to-heaven-board-list-mobile-card-title-unique">{letter.title}</p>
                  <p className="letter-to-heaven-board-list-mobile-card-sender-unique">From {letter.sender} | {formatDate(letter.date)}</p>
                </div>
              </div>
            )) : <p>{isSearchActive ? '검색 결과가 없습니다.' : 'No letters available'}</p>}
        </div>

        {/* Load More */}
        {/* {!isSearchActive && hasMoreLetters && !loading && (
          <div className="letter-to-heaven-board-list-mobile-more-section-unique">
            <button className="letter-to-heaven-board-list-mobile-more-btn-unique" onClick={handleLoadMore}>더보기</button>
          </div>
        )} */}
        {/* More Button (always visible) */}
        <div className="letter-to-heaven-board-list-mobile-more-section-unique">
          <button
            className="letter-to-heaven-board-list-mobile-more-btn-unique"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? '로딩 중...' : '더보기'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default LetterToHeavenBoardListMobile;
